import Heading from '@atlaskit/heading';
import { Box, Stack, xcss } from '@atlaskit/primitives';
import { DatabaseLayoutType } from '@databases/types/database/view';
import React, { FunctionComponent, useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import useView from '../../hooks/useView';
import { messages } from '../../i18n';

import BoardLayoutOptions from './BoardLayoutOptions';
import CalendarLayoutOptions from './CalendarLayoutOptions';
import CardLayoutOptions from './CardLayoutOptions';
import TableLayoutOptions from './TableLayoutOptions';
import ViewSettingsLayoutTypePicker from './ViewSettingsLayoutTypePicker';

const HeadingStyles = xcss({
  paddingTop: 'space.100',
  paddingBottom: 'space.200',
  paddingLeft: 'space.150',
  borderBottomWidth: '2px',
  borderBottomStyle: 'solid',
  borderBottomColor: 'color.border',
});

export const getLayoutOptionTitle = (layoutType: DatabaseLayoutType) => {
  switch (layoutType) {
    case DatabaseLayoutType.TABLE:
      return messages['layout.table-text'];
    case DatabaseLayoutType.CARD:
      return messages['layout.card-text'];
    case DatabaseLayoutType.BOARD:
      return messages['layout.board-text'];
    case DatabaseLayoutType.CALENDAR:
      return messages['layout.calendar-text'];
  }
};

export interface ViewSettingsLayoutProps {
  FieldPicker: React.FunctionComponent<any>;
  contentId: string;
}

const ViewSettingsLayout: FunctionComponent<ViewSettingsLayoutProps> = ({
  FieldPicker,
  contentId,
}) => {
  const { view, setViewChange } = useView();
  const { formatMessage } = useIntl();
  const layout = view.settings.layout;

  const LayoutOptions = useCallback(() => {
    switch (layout.type) {
      case DatabaseLayoutType.TABLE:
        return <TableLayoutOptions />;
      case DatabaseLayoutType.CARD:
        return (
          <CardLayoutOptions
            onChange={setViewChange}
            FieldPicker={FieldPicker}
            contentId={contentId}
            layout={layout}
            source={'databaseViewSettingsPanel'}
          />
        );
      case DatabaseLayoutType.BOARD:
        return (
          <BoardLayoutOptions
            onChange={setViewChange}
            FieldPicker={FieldPicker}
            contentId={contentId}
            layout={layout}
            source={'databaseViewSettingsPanel'}
          />
        );
      case DatabaseLayoutType.CALENDAR:
        return (
          <CalendarLayoutOptions
            FieldPicker={FieldPicker}
            contentId={contentId}
            source={'databaseViewSettingsPanel'}
          />
        );
    }
  }, [layout, setViewChange, FieldPicker, contentId]);

  return (
    <Stack space="space.300">
      <ViewSettingsLayoutTypePicker isCompact={true} source="databaseViewSettingsPanel" />
      <Box xcss={HeadingStyles}>
        <Heading as="h3" size="xsmall">
          {formatMessage(messages['view-settings-panel.layout.layout-options-text'], {
            layoutType: formatMessage(getLayoutOptionTitle(layout.type)),
          })}
        </Heading>
      </Box>
      <LayoutOptions />
    </Stack>
  );
};

export default ViewSettingsLayout;
